import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {distributorT} from '@/lib/types/distributor.types';
import {statsRowT} from '@/lib/types/stats.types';

import {renderDistributorTableStats} from '@/lib/config/distributorTableConfig';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useBrand} from '@/pages/(shared)/brands/detail/providers/brandDetailsProvider';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent} from '@/components/confirmDeleteModalComponent';
import {DataTable, DataTableRefT} from '@/components/dataTable';
import {SearchAndSelectComponent} from '@/components/searchAndSelectComponent';
import {StatsRowComponent} from '@/components/statsRowComponent';

export const BrandDetailDistributors = () => {
  const {isAdmin} = useAuth();
  const {brandId, addDistributorToBrand, removeDistributorFromBrand, searchDistributorsToAdd} = useBrand();
  const {data: stats} = useViewQuery({view: 'distributor_stats'});
  const {toast} = useNotifications();
  // State
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<distributorT[] | []>([]);

  // Refs
  const dataTableRef = useRef<DataTableRefT | null>(null);
  const dataRowToDeleteRef = useRef<distributorT | null>(null);

  // Data Table Config
  const dataTableConfig = createDataTableConfig({
    enabled: !!brandId,
    table: 'Distributor_Brand',
    title: 'Distributors Summary',
    columns: [
      'fk_brand_id',
      'date_removed',
      ['Distributor', ['distributor_id', 'distributor_name', 'distributor_city', 'distributor_state']],
    ],
    searchColumn: 'Distributor.distributor_name',
    filters: [{column: 'fk_brand_id', operator: 'eq', value: brandId}],
    addButtonLabel: 'Add Distributor To Brand',
    noResultsMessage: 'No distributors in brand',
    preventScrolling: true,
    onAdd: () => setSearchModalVisible(true),
    columnsDisplay: [
      {
        header: 'Status',
        field: 'date_removed',
        render: row => (
          <span className={`badge bg-${!row.date_removed ? 'success' : 'danger'} text-white`}>
            {!row.date_removed ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Name',
        field: 'Distributor.distributor_name',
        render: row => (
          <Link
            className="text-primary hover:text-primary dark:text-primary"
            to={`/distributors/${row.Distributor.distributor_id}`}
          >
            {row.Distributor.distributor_name}
          </Link>
        ),
      },
      {
        header: 'Location',
        field: 'Distributor.distributor_city',
        render: row => `${row.Distributor.distributor_city}, ${row.Distributor.distributor_state}`,
      },
    ],
    actionsDisplay: [
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: async row => {
          dataRowToDeleteRef.current = row.Distributor;
          setDeleteModalVisible(true);
        },
        permission: () => isAdmin,
      },
    ],
  });

  // Stats
  const statsRow: statsRowT = renderDistributorTableStats(stats?.total, stats?.active, stats?.inactive);

  // Functions
  // Function used to close the search modal
  const handleCloseSearchModal = () => {
    setSearchModalVisible(false);
    setSearchResults([]);
  };

  // Function used to get the search results
  const onSearch = async (query: string) => {
    const results = await searchDistributorsToAdd(query);
    setSearchResults(results);
  };

  // Function used to add a row
  const handleAdd = async (distributor: distributorT) => {
    try {
      await addDistributorToBrand(distributor.distributor_id);
      toast.success({
        title: 'Distributor added to Brand',
        message: `Distributor ${distributor.distributor_name} added to Brand`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Adding Distributor To Brand',
        message: (error as Error).message,
      });
    } finally {
      setSearchResults([]);
      setSearchModalVisible(false);
    }
  };

  // Function used to delete a row
  const onDeleteConfirm = async () => {
    if (!dataRowToDeleteRef.current) throw new Error('Distributor not found');

    try {
      await removeDistributorFromBrand(dataRowToDeleteRef.current.distributor_id);
      toast.success({
        title: 'Distributor Removed from Brand',
        message: `Distributor ${dataRowToDeleteRef.current.distributor_name} removed from Brand`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Removing Distributor From Brand',
        message: (error as Error).message,
      });
    } finally {
      setDeleteModalVisible(false);
      dataRowToDeleteRef.current = null;
      dataTableRef.current?.refetch();
    }
  };

  return (
    <>
      <StatsRowComponent stats={statsRow} />
      <DataTable ref={dataTableRef} {...dataTableConfig} />
      <SearchAndSelectComponent
        title="Search and Select Distributors"
        isVisible={searchModalVisible}
        onClose={handleCloseSearchModal}
        onSelect={handleAdd}
        onSearch={onSearch}
        results={searchResults}
        mapStructure={{
          displayFields: [{title: 'Distributor Name', field: 'distributor_name'}],
          valueField: 'distributor_id',
        }}
      />
      <ConfirmDeleteModalComponent
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        title="Confirm Deletion"
        description="Are you sure you want to remove this distributor from the brand?"
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
